// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Plus, Search, Edit, Eye, Trash2, ArrowUpDown } from 'lucide-react';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';

interface TipoLocal {
  id: string;
  nome: string;
  descricao?: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export default function TipoLocal() {
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [tiposLocal, setTiposLocal] = useState<TipoLocal[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<keyof TipoLocal>('nome');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTipo, setSelectedTipo] = useState<TipoLocal | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [newTipo, setNewTipo] = useState({
    nome: '',
    descricao: ''
  });

  useEffect(() => {
    if (hasPermission('locais', 'tipo_local', 'read')) {
      fetchTiposLocal();
    } else {
      setLoading(false);
    }
  }, [hasPermission]);

  useEffect(() => {
    if (hasPermission('locais', 'tipo_local', 'read')) {
      fetchTiposLocal();
    }
  }, [sortBy, sortOrder]);

  const fetchTiposLocal = async () => {
    if (!hasPermission('locais', 'tipo_local', 'read')) return;
    
    try {
      let query = supabase
        .from('tipo_local')
        .select('*');

      // Apply search filter
      if (search.trim()) {
        query = query.or(`nome.ilike.%${search}%,descricao.ilike.%${search}%`);
      }

      // Apply sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      const { data, error } = await query;

      if (error) throw error;

      setTiposLocal(data || []);
    } catch (error: any) {
      console.error('Erro ao buscar tipos de local:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os tipos de local",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: keyof TipoLocal) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleSearch = () => {
    if (hasPermission('locais', 'tipo_local', 'read')) {
      fetchTiposLocal();
    }
  };

  const handleCreate = async () => {
    if (!newTipo.nome.trim()) {
      toast({
        title: "Erro",
        description: "Nome é obrigatório",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('tipo_local')
        .insert([{
          nome: newTipo.nome.trim(),
          descricao: newTipo.descricao.trim() || null,
          created_by: (await supabase.auth.getUser()).data.user?.id
        }]);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Tipo de local criado com sucesso",
      });

      setNewTipo({ nome: '', descricao: '' });
      setIsCreateModalOpen(false);
      
      // Atualizar a lista
      await fetchTiposLocal();
    } catch (error: any) {
      console.error('Erro ao criar tipo de local:', error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o tipo de local",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tipo_local')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Tipo de local excluído com sucesso",
      });

      // Atualizar a lista
      await fetchTiposLocal();
    } catch (error: any) {
      console.error('Erro ao excluir tipo de local:', error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o tipo de local",
        variant: "destructive",
      });
    }
  };

  if (!hasPermission('locais', 'tipo_local', 'read')) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-muted-foreground">
              Você não tem permissão para visualizar tipos de local.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Tipos de Local</h1>
        {hasPermission('locais', 'tipo_local', 'write') && (
          <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Novo Tipo
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Novo Tipo de Local</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="nome">Nome *</Label>
                  <Input
                    id="nome"
                    value={newTipo.nome}
                    onChange={(e) => setNewTipo(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="Digite o nome do tipo de local"
                  />
                </div>
                <div>
                  <Label htmlFor="descricao">Descrição</Label>
                  <Textarea
                    id="descricao"
                    value={newTipo.descricao}
                    onChange={(e) => setNewTipo(prev => ({ ...prev, descricao: e.target.value }))}
                    placeholder="Digite a descrição (opcional)"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsCreateModalOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button onClick={handleCreate}>
                    Criar Tipo
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Buscar e Filtrar</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome ou descrição..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tipos de Local Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tipos de Local</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : tiposLocal.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum tipo de local encontrado
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort('nome')}
                      className="h-auto p-0 font-medium"
                    >
                      Nome
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort('descricao')}
                      className="h-auto p-0 font-medium"
                    >
                      Descrição
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort('ativo')}
                      className="h-auto p-0 font-medium"
                    >
                      Status
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort('created_at')}
                      className="h-auto p-0 font-medium"
                    >
                      Criado em
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort('updated_at')}
                      className="h-auto p-0 font-medium"
                    >
                      Atualizado em
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tiposLocal.map((tipo) => (
                  <TableRow key={tipo.id}>
                    <TableCell className="font-medium">{tipo.nome}</TableCell>
                    <TableCell>
                      {tipo.descricao ? (
                        <span className="text-sm">{tipo.descricao}</span>
                      ) : (
                        <span className="text-muted-foreground italic">Sem descrição</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={tipo.ativo ? "default" : "secondary"}>
                        {tipo.ativo ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {format(new Date(tipo.created_at), 'dd/MM/yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      {format(new Date(tipo.updated_at), 'dd/MM/yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        {/* View Details */}
                        <AlertDialog open={isDetailModalOpen && selectedTipo?.id === tipo.id} onOpenChange={(open) => {
                          setIsDetailModalOpen(open);
                          if (!open) setSelectedTipo(null);
                        }}>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedTipo(tipo);
                                setIsDetailModalOpen(true);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Detalhes do Tipo de Local</AlertDialogTitle>
                              <AlertDialogDescription asChild>
                                <div className="space-y-4 text-left">
                                  <div>
                                    <strong>Nome:</strong> {selectedTipo?.nome}
                                  </div>
                                  <div>
                                    <strong>Descrição:</strong> {selectedTipo?.descricao || 'Sem descrição'}
                                  </div>
                                  <div>
                                    <strong>Status:</strong> {' '}
                                    <Badge variant={selectedTipo?.ativo ? "default" : "secondary"}>
                                      {selectedTipo?.ativo ? 'Ativo' : 'Inativo'}
                                    </Badge>
                                  </div>
                                  <div>
                                    <strong>Criado em:</strong> {selectedTipo && format(new Date(selectedTipo.created_at), 'dd/MM/yyyy HH:mm')}
                                  </div>
                                  <div>
                                    <strong>Atualizado em:</strong> {selectedTipo && format(new Date(selectedTipo.updated_at), 'dd/MM/yyyy HH:mm')}
                                  </div>
                                </div>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogAction>Fechar</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>

                        {/* Edit */}
                        {hasPermission('locais', 'tipo_local', 'write') && (
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}

                        {/* Delete */}
                        {hasPermission('locais', 'tipo_local', 'admin') && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja excluir o tipo de local "{tipo.nome}"?
                                  Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(tipo.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}